export type UUID = string;
export type IsoDateString = string;
export type TextDirection = 'ltr' | 'rtl';

export const TESTING_USER_TEXT_PREFIX = '--testing--';
export const TESTING_ALLOWED_USER_IDS: readonly string[] = ['63643842-f011-7041-9084-1133fb0197dd'];
export const TESTING_SIMULATED_LLM_DELAY_MS = 20000;

export interface IConnectionAndUsername {
  connectionId: string;
  username: string;
}

export interface IInitializeNewConnectedClientParams {
  handlerSubsegment?: any;
  connectedUserId: string;
  connectedUserEmail: string;
  connectionsAndUsernames: IConnectionAndUsername[];
}

export enum RecordAccessLevel {
  Private = 0,
  Restricted = 1,
  Public = 2,
}

export interface ILLMProfile {
  profile_id: UUID;
  model_id: string;
  description: string;
  level?: string | null;
  disabled?: boolean;
  priority?: number;
  notes?: string | null;
  created_at?: IsoDateString;
  updated_at?: IsoDateString;
}

export interface ILLMPrompt {
  prompt_id: UUID;
  name: string;
  user_prompt: string;
  system_prompt: string;
  created_at?: IsoDateString;
  updated_at?: IsoDateString;
}

export interface IRecord {
  record_id: UUID;
  user_text: string;
  ai_text: string;
  subject: string;
  llm_profile_id?: UUID | null;
  llm_prompt_id?: UUID | null;
  access_level?: number;
  is_archived?: boolean;
  saas_tenant_id: UUID;
  saas_tenant_name?: string | null;
  created_at?: IsoDateString;
  updated_at?: IsoDateString;
}

export interface ISubrecord {
  subrecord_id: UUID;
  record_id: UUID;
  user_text: string;
  ai_text?: string | null;
  non_ai?: boolean;
  is_consolidated?: boolean;
  saas_tenant_id: UUID;
  saas_tenant_name?: string | null;
  created_at?: IsoDateString;
}

export interface ISummary {
  id: UUID;
  record_id: UUID;
  subject?: string | null;
  content: string;
  summary: string;
  created_at?: IsoDateString;
  saas_tenant_id: UUID;
  saas_tenant_name?: string | null;
  direction?: TextDirection;
}

export interface ISaasTenant {
  saas_tenant_id: UUID;
  disabled?: boolean;
  credit?: number;
  email?: string | null;
  name?: string | null;
  phone?: string | null;
  address?: string | null;
  israeli_id?: string | null;
  created_at?: IsoDateString;
  updated_at?: IsoDateString;
}

export interface ILLMPricing {
  model_id: string;
  input_cost_per_million: number;
  output_cost_per_million: number;
  created_at?: IsoDateString;
  updated_at?: IsoDateString;
}

export interface IUsageMetric {
  usage_id: UUID;
  saas_tenant_id: UUID;
  llm_profile_id?: UUID | null;
  prompt_tokens: number;
  completion_tokens: number;
  reasoning_tokens: number;
  cost: number;
  created_at?: IsoDateString;
}

export type RedisKeysScope = 'all' | 'websocket';

export interface IRedisKeyItem {
  key: string;
  type: string;
  value: any;
}

export interface IRedisKeysResponse {
  items: IRedisKeyItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export function parseRedisKeysScope(scopeRaw?: string | null): RedisKeysScope {
  const value = (scopeRaw ?? 'all').toLowerCase();
  if (value === 'websocket') return 'websocket';
  return 'all';
}

export interface IDocument {
  document_id: UUID;
  file_name: string;
  file_key: string;
  file_type: string;
  file_size: number;
  saas_tenant_id: UUID;
  created_at: IsoDateString;
  updated_at?: IsoDateString;
}
